/**
 * Move-In Orchestration API Client
 * Typed client for the 6 move-in endpoints under /landlord/api/units/:unitId/move-in
 */

import { apiFetch } from './api.core';
import type { MoveInProjection, TenantPayload, CreateTenantsResponse } from '../types/move-in';

/**
 * GET /landlord/api/units/:unitId/move-in
 * Returns the move-in projection (steps, link, suggestions)
 */
export async function getMoveIn(unitId: string): Promise<MoveInProjection> {
  return apiFetch(`/landlord/api/units/${unitId}/move-in`);
}

/**
 * POST /landlord/api/units/:unitId/move-in/link
 * Bind an accepted lead application to the unit's move-in workflow
 */
export async function bindMoveInLink(unitId: string, leadId: string): Promise<void> {
  await apiFetch(`/landlord/api/units/${unitId}/move-in/link`, {
    method: 'POST',
    body: JSON.stringify({ leadId }),
  });
}

/**
 * DELETE /landlord/api/units/:unitId/move-in/link
 * Unbind the linked lead from the unit's move-in workflow
 */
export async function unbindMoveInLink(unitId: string): Promise<void> {
  await apiFetch(`/landlord/api/units/${unitId}/move-in/link`, {
    method: 'DELETE',
  });
}

/**
 * POST /landlord/api/units/:unitId/move-in/tenants
 * Create tenant records on the unit (no lease required). Returns counts of
 * created and skipped (already-existing) tenants.
 */
export async function createMoveInTenants(
  unitId: string,
  tenants: TenantPayload[]
): Promise<CreateTenantsResponse> {
  return apiFetch(`/landlord/api/units/${unitId}/move-in/tenants`, {
    method: 'POST',
    body: JSON.stringify({ tenants }),
  });
}

/**
 * POST /landlord/api/units/:unitId/move-in/suggestions/:id/dismiss
 * Permanently dismiss a move-in suggestion
 */
export async function dismissMoveInSuggestion(
  unitId: string,
  suggestionId: string
): Promise<void> {
  await apiFetch(
    `/landlord/api/units/${unitId}/move-in/suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
    { method: 'POST' }
  );
}

/**
 * POST /landlord/api/units/:unitId/move-in/suggestions/:id/snooze
 * Snooze a move-in suggestion for 1, 3, 7, or 30 days
 */
export async function snoozeMoveInSuggestion(
  unitId: string,
  suggestionId: string,
  days: 1 | 3 | 7 | 30
): Promise<void> {
  await apiFetch(
    `/landlord/api/units/${unitId}/move-in/suggestions/${encodeURIComponent(suggestionId)}/snooze`,
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}
